import React from 'react';

interface DatePickerProps {
  selectedDate: string;
  onChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  className?: string;
  mode?: 'birthdate' | 'future';
}

const DatePicker: React.FC<DatePickerProps> = ({
  selectedDate,
  onChange,
  minDate,
  maxDate,
  className = '',
  mode = 'birthdate'
}) => {
  // Parse the selected date or use empty values
  const parseDate = (dateString: string) => {
    if (!dateString) return { day: '', month: '', year: '' };
    const [year, month, day] = dateString.split('-');
    return { day: day || '', month: month || '', year: year || '' };
  };

  const { day, month, year } = parseDate(selectedDate);

  // Generate arrays for dropdowns
  const days = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const currentYear = new Date().getFullYear();

  // Generate years based on mode
  const years = mode === 'future'
    ? Array.from({ length: 5 }, (_, i) => (currentYear + i).toString()) // Current year + 4 future years
    : Array.from({ length: 100 }, (_, i) => (currentYear - 18 - i).toString()); // Birth dates (18+ years ago)

  // Handle individual field changes
  const handleChange = (field: 'day' | 'month' | 'year', value: string) => {
    const currentValues = parseDate(selectedDate);
    const newValues = { ...currentValues, [field]: value };

    // Build the date string with current values
    if (newValues.day && newValues.month && newValues.year) {
      // All fields filled - validate and update
      const dateStr = `${newValues.year}-${newValues.month}-${newValues.day}`;
      const date = new Date(dateStr);

      // Check if date is valid
      if (!isNaN(date.getTime())) {
        onChange(dateStr);
      } else {
        // Invalid date but update anyway to show user's selection
        onChange(dateStr);
      }
    } else {
      // Partial date - still update so selections are preserved
      const partialDate = `${newValues.year || '0000'}-${newValues.month || '00'}-${newValues.day || '00'}`;
      onChange(partialDate);
    }
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      {/* Day */}
      <select
        value={day}
        onChange={(e) => {
          e.stopPropagation();
          handleChange('day', e.target.value);
        }}
        onClick={(e) => e.stopPropagation()}
        className="flex-1 p-3 bg-primary-100 border-2 border-blue-400/50 hover:border-blue-400/70 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast cursor-pointer transition-all"
      >
        <option value="">Day</option>
        {days.map((d) => (
          <option key={d} value={d}>
            {parseInt(d)}
          </option>
        ))}
      </select>

      {/* Month */}
      <select
        value={month}
        onChange={(e) => {
          e.stopPropagation();
          handleChange('month', e.target.value);
        }}
        onClick={(e) => e.stopPropagation()}
        className="flex-[2] p-3 bg-primary-100 border-2 border-blue-400/50 hover:border-blue-400/70 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast cursor-pointer transition-all"
      >
        <option value="">Month</option>
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>

      {/* Year */}
      <select
        value={year}
        onChange={(e) => {
          e.stopPropagation();
          handleChange('year', e.target.value);
        }}
        onClick={(e) => e.stopPropagation()}
        className="flex-1 p-3 bg-primary-100 border-2 border-blue-400/50 hover:border-blue-400/70 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast cursor-pointer transition-all"
      >
        <option value="">Year</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
};

export default DatePicker;
